export type PageFeeConfig = {
  amount?: number | null;
  percentage?: number | null;
  multiplyByQuantity?: boolean | null;
  multiplyByTickets?: boolean | null;
};

export type PageFeeAmountInput = {
  fee: PageFeeConfig;
  amount: number;
  pageType?: string | null;
  quantity?: number;
  numberOfTickets?: number;
  round: (value: number) => number;
};

export type PageFeeAmounts = {
  amount: number;
  percentageAmount: number;
  fixedAmount: number;
};

export function isCombinedFee(fee: PageFeeConfig): boolean {
  return Boolean(fee.percentage && fee.percentage > 0 && fee.amount && fee.amount > 0);
}

// The multiply flags scale only the fixed part: the percentage already grows with the amount.
const getFixedAmount = ({
  fee,
  pageType,
  quantity,
  numberOfTickets,
  round,
}: Omit<PageFeeAmountInput, 'amount'>) => {
  const fixed = fee.amount ?? 0;
  if (pageType === 'checkout' && fee.multiplyByQuantity) return round(fixed * (quantity as number));
  if (pageType === 'event' && fee.multiplyByTickets)
    return round(fixed * (numberOfTickets as number));
  return round(fixed);
};

export function getPageFeeAmounts({
  fee,
  amount,
  pageType,
  quantity,
  numberOfTickets,
  round,
}: PageFeeAmountInput): PageFeeAmounts {
  if (isCombinedFee(fee)) {
    const percentageAmount = round(((fee.percentage as number) / 100) * amount);
    const fixedAmount = getFixedAmount({ fee, pageType, quantity, numberOfTickets, round });
    return { amount: percentageAmount + fixedAmount, percentageAmount, fixedAmount };
  }

  if (fee.percentage) {
    const percentageAmount = round((fee.percentage / 100) * amount);
    return { amount: percentageAmount, percentageAmount, fixedAmount: 0 };
  }

  const fixedAmount = getFixedAmount({ fee, pageType, quantity, numberOfTickets, round });
  return { amount: fixedAmount, percentageAmount: 0, fixedAmount };
}

// The rate printed after a fee's name: "10%", or "10% + $0.30" for a combined fee, so the line explains its total.
export function getFeeRate(
  fee: { percentage?: number | null; fixedAmount?: number | null },
  formatFixedAmount: (amount: number) => string,
): string | null {
  if (!fee.percentage) return null;
  return fee.fixedAmount
    ? `${fee.percentage}% + ${formatFixedAmount(fee.fixedAmount)}`
    : `${fee.percentage}%`;
}

export function getFeeLabel(
  fee: { name?: string | null; percentage?: number | null; fixedAmount?: number | null },
  formatFixedAmount: (amount: number) => string,
): string {
  const rate = getFeeRate(fee, formatFixedAmount);
  return `${fee.name ?? 'Fee'}${rate ? ` (${rate})` : ''}`;
}
